import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Printer, AlertTriangle, CheckCircle2, FileX } from "lucide-react";

const FIELD_LABELS = [
  ["ro_number", "RO Number"], ["vin", "VIN"], ["ro_date", "RO Date"],
  ["customer_name", "Customer"], ["vehicle_year", "Year"], ["vehicle_make", "Make"],
  ["vehicle_model", "Model"], ["service_advisor", "Advisor"], ["technician_id", "Technician"],
  ["signature_status", "Signature"], ["mileage_in", "Mileage In"], ["mileage_out", "Mileage Out"],
  ["labor_op_code", "Labor Op"], ["labor_hours", "Labor Hrs"], ["claim_amount", "Claim $"],
];

const esc = (v) => String(v ?? "—").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

const FailedFiles = ({ failures }) => (
  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4" data-testid="failed-files-list">
    <p className="flex items-center gap-2 font-heading text-lg font-bold text-red-300">
      <FileX className="h-4 w-4" /> Could not be read ({failures.length}) — please retry these
    </p>
    <ul className="mt-2 grid gap-1.5">
      {failures.map((f, i) => (
        <li key={i} className="rounded-md border border-red-500/20 bg-background/40 px-2.5 py-1.5 text-xs" data-testid={`failed-file-${i}`}>
          <div className="font-mono font-semibold text-foreground">{f.name}</div>
          <div className="mt-0.5 text-red-200/80">{f.reason}</div>
        </li>
      ))}
    </ul>
  </div>
);

export function AuditFindingsDialog({ open, onOpenChange, records = [], failures = [] }) {
  const printFindings = () => {
    const win = window.open("", "_blank", "width=920,height=740");
    if (!win) return;
    const blocks = records.map((r) => {
      const flags = (r.flags || []).length
        ? r.flags.map((f) => `<li>${esc(f)}</li>`).join("")
        : "<li style='color:#059669'>No issues detected</li>";
      const cs = [["Complaint", r.complaint], ["Cause", r.cause], ["Correction", r.correction]]
        .map(([k, v]) => `<p style="margin:2px 0"><strong>${k}:</strong> ${esc(v)}</p>`).join("");
      const cells = FIELD_LABELS.map(([k, l]) =>
        `<td style="padding:5px 8px;border:1px solid #ddd;vertical-align:top"><small style="color:#777">${l}</small><br>${esc(r[k])}</td>`);
      let grid = "";
      for (let i = 0; i < cells.length; i += 3) grid += `<tr>${cells.slice(i, i + 3).join("")}</tr>`;
      return `<div style="page-break-after:always;margin-bottom:26px">
        <h2 style="margin:0 0 2px">Repair Order #${esc(r.ro_number)}</h2>
        <p style="color:#666;margin:0 0 8px;font-size:12px">Warranty Administrator: ${esc(r.owner_name)} · Source: ${esc(r.source_filename)} · Status: ${esc(r.compliance_status)}</p>
        <table style="border-collapse:collapse;width:100%;font-size:12px">${grid}</table>
        <h3 style="margin:14px 0 4px">3 C's</h3>${cs}
        <h3 style="margin:14px 0 4px">Audit Findings (${(r.flags || []).length})</h3>
        <ul style="margin:4px 0 0">${flags}</ul></div>`;
    }).join("");
    win.document.write(`<html><head><title>Audit Findings Summary</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;padding:28px;color:#111">
      <h1 style="margin:0">Warranty Compliance — Audit Findings</h1>
      <p style="color:#666;margin:4px 0 20px">Generated ${new Date().toLocaleString()} · ${records.length} repair order(s)</p>
      ${blocks}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-card" data-testid="findings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Audit Findings</DialogTitle>
          <DialogDescription>
            {records.length} repair order{records.length !== 1 ? "s" : ""} scanned{failures.length ? `, ${failures.length} could not be read` : ""}. Auto-detected compliance issues are shown below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {failures.length > 0 && <FailedFiles failures={failures} />}
          {records.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-secondary/30 p-4" data-testid={`finding-${r.ro_number || r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-heading text-lg font-bold">RO #{r.ro_number || "—"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {[r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ") || "vehicle n/a"} · {r.source_filename}
                  </p>
                </div>
                <StatusBadge status={r.compliance_status} />
              </div>
              {r.score != null && (
                <p className="mt-2 font-mono text-xs text-muted-foreground">Compliance score: <span className="font-bold text-foreground">{r.score}/100</span></p>
              )}
              {r.flags?.length ? (
                <ul className="mt-3 grid gap-1.5">
                  {(r.findings_detail?.length ? r.findings_detail : r.flags.map((t) => ({ title: t }))).map((fd, i) => (
                    <li key={i} className="rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-200">
                      <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-3 w-3 shrink-0" /> {fd.title}</div>
                      {fd.action && <div className="mt-0.5 pl-5 text-[11px] text-amber-200/70">{fd.action}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {r.compliance_status === "skip_maintenance" ? "Maintenance — no warranty review required" : "No issues detected"}</p>
              )}
              {r.packs?.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {r.packs.map((p, i) => (
                    <span key={i} className="rounded border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300">{p.label || p}</span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="findings-close">Close</Button>
          <Button onClick={printFindings} data-testid="print-findings-button"><Printer className="h-4 w-4" /> Print Summary</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
